import { useEffect, useState } from "react";
import { CreditCard, Download, Receipt, TriangleAlert, Paperclip } from "lucide-react";
import { api, apiError, descargarFacturaPdf, facturaBlobUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, fecha, diasRestantes } from "@/lib/format";
import { Loading, PageTitle, Stat, Empty } from "@/components/Common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Finanzas() {
  const { activeId, activeStudent } = useAuth();
  const [cuenta, setCuenta] = useState(null);
  const [plan, setPlan] = useState(null);
  const [cuentas, setCuentas] = useState([]);
  const [pagar, setPagar] = useState(null);
  const [ver, setVer] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [metodo, setMetodo] = useState("tarjeta");
  const [transferencia, setTransferencia] = useState({
    referencia: "", titular: "", comprobante_nombre: "", comprobante_base64: "",
  });
  const [busy, setBusy] = useState(false);

  const cargar = () => {
    api.get(`/estado-cuenta/${activeId}`).then((r) => setCuenta(r.data));
    api.get(`/estudiantes/${activeId}/asignaciones`)
      .then((r) => setPlan(r.data.plan_pago))
      .catch(() => setPlan(null));
    api.get("/configuracion").then((r) => setCuentas(r.data.cuentas || [])).catch(() => setCuentas([]));
  };

  useEffect(() => {
    if (!activeId) return;
    setCuenta(null);
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const subirComprobante = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("El archivo no debe superar los 5 MB");
    const reader = new FileReader();
    reader.onload = () => setTransferencia({
      ...transferencia, comprobante_nombre: file.name, comprobante_base64: reader.result,
    });
    reader.readAsDataURL(file);
  };

  const confirmar = async () => {
    if (metodo === "transferencia" && !transferencia.comprobante_base64) {
      return toast.error("Adjunte la foto o el PDF del comprobante de transferencia");
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/facturas/${pagar.id}/pagar`, {
        metodo,
        referencia: metodo === "transferencia" ? transferencia.referencia || null : null,
        titular: metodo === "transferencia" ? transferencia.titular || null : null,
        comprobante_nombre: metodo === "transferencia" ? transferencia.comprobante_nombre : null,
        comprobante_base64: metodo === "transferencia" ? transferencia.comprobante_base64 : null,
      });
      toast.success(data.estado === "en_verificacion"
        ? "Comprobante enviado. La administración validará su pago y le notificará."
        : `Pago aplicado. Comprobante ${pagar.ncf} disponible.`);
      setPagar(null);
      setTransferencia({ referencia: "", titular: "", comprobante_nombre: "", comprobante_base64: "" });
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const descargar = async (f) => {
    try {
      await descargarFacturaPdf(f.id, f.ncf);
    } catch (e) {
      toast.error(apiError(e, "No se pudo descargar el comprobante"));
    }
  };

  const visualizar = async (f) => {
    setVer(f);
    setPdfUrl(null);
    try {
      setPdfUrl(await facturaBlobUrl(f.id));
    } catch (e) {
      toast.error(apiError(e, "No se pudo abrir el comprobante"));
    }
  };

  if (!activeId) return <Empty>No hay estudiantes asociados.</Empty>;
  if (!cuenta) return <Loading />;

  return (
    <div data-testid="finance-page">
      <PageTitle title="Estado de cuenta"
                 subtitle={`Cargos, descuentos y comprobantes fiscales de ${activeStudent?.nombre}.`} />

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Stat testId="fin-balance" label="Balance pendiente" value={money(cuenta.balance_pendiente)}
              tone={cuenta.balance_pendiente > 0 ? "warn" : "good"} />
        <Stat testId="fin-pagado" label="Total pagado" value={money(cuenta.total_pagado)} tone="good" />
        <Stat testId="fin-descuentos" label="Descuentos aplicados" value={money(cuenta.total_descuentos)} />
        <Stat testId="fin-morosidad" label="Morosidad" value={money(cuenta.morosidad)}
              tone={cuenta.morosidad > 0 ? "bad" : "good"} hint={`${cuenta.cantidad_vencidas} factura(s) vencida(s)`} />
      </div>

      {plan && (
        <div className="mt-8 rounded-lg border border-primary/30 bg-primary/[0.04] p-6 sm:p-8" data-testid="modalidad-pago">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Modalidad de pago asignada</p>
          <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-2xl font-bold tracking-tight">{plan.nombre}</h2>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-muted-foreground">{plan.descripcion}</p>
            </div>
            <div className="text-right">
              {plan.descuento_pct > 0 && (
                <p className="text-sm text-muted-foreground line-through">{money(plan.monto_base)}</p>
              )}
              <p className="font-display text-3xl font-extrabold tracking-tight text-primary">
                {money(plan.monto_base * (1 - (plan.descuento_pct || 0) / 100))}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {plan.cuotas > 1
                  ? `${plan.cuotas} cuotas de ${money((plan.monto_base * (1 - (plan.descuento_pct || 0) / 100)) / plan.cuotas)}`
                  : "pago único"}
                {plan.descuento_pct > 0 ? ` · ${plan.descuento_pct}% descuento` : ""}
              </p>
            </div>
          </div>
        </div>
      )}

      {cuenta.morosidad > 0 && (
        <div className="mt-8 flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-6"
             data-testid="alerta-morosidad">
          <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <p className="text-sm leading-relaxed">
            Existen facturas vencidas. Mientras haya morosidad, el boletín de calificaciones permanecerá bloqueado
            según las políticas del centro.
          </p>
        </div>
      )}

      <div className="mt-8 overflow-hidden rounded-lg border border-border/60 bg-card">
        <div className="flex items-center gap-2 border-b border-border px-6 py-4">
          <Receipt className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-semibold">Histórico de facturas</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="tabla-facturas">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-6 py-3">NCF</th>
                <th className="px-6 py-3">Concepto</th>
                <th className="px-6 py-3">Vencimiento</th>
                <th className="px-6 py-3">Descuento</th>
                <th className="px-6 py-3">Total</th>
                <th className="px-6 py-3">Estado</th>
                <th className="px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {cuenta.facturas.map((f) => {
                const dias = diasRestantes(f.fecha_vencimiento);
                const vencida = f.estado === "pendiente" && dias < 0;
                return (
                  <tr key={f.id} className="border-t border-border/60 hover:bg-muted/30" data-testid={`factura-row-${f.ncf}`}>
                    <td className="px-6 py-4 font-mono text-xs">
                      {f.ncf}
                      {f.fiscal === false && (
                        <span className="ml-2 rounded-full border border-destructive/40 px-2 py-0.5 text-[10px] text-destructive"
                              data-testid={`sin-valor-fiscal-${f.ncf}`}>
                          sin valor fiscal
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">{f.detalle?.[0]?.concepto}</td>
                    <td className="px-6 py-4 font-mono text-xs">{fecha(f.fecha_vencimiento)}</td>
                    <td className="px-6 py-4 font-mono text-xs">{money(f.monto_descuento)}</td>
                    <td className="px-6 py-4 font-mono font-semibold">
                      {money(f.monto_total)}
                      {f.monto_pagado > 0 && f.estado !== "pagado" && (
                        <span className="mt-1 block text-[10px] font-normal text-muted-foreground"
                              data-testid={`abonado-${f.ncf}`}>
                          abonado {money(f.monto_pagado)} · balance {money(f.monto_total - f.monto_pagado)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={f.estado === "pagado" ? "secondary"
                        : f.estado === "en_verificacion" ? "default" : vencida ? "destructive" : "outline"}>
                        {f.estado === "pagado" ? "pagado"
                          : f.estado === "en_verificacion" ? "en verificación"
                            : vencida ? "vencida" : "pendiente"}
                      </Badge>
                      {f.motivo_rechazo && (
                        <span className="mt-1 block text-[10px] text-destructive" data-testid={`rechazo-${f.ncf}`}>
                          Pago rechazado: {f.motivo_rechazo}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        {f.estado === "pendiente" && (
                          <Button size="sm" className="rounded-full" onClick={() => setPagar(f)}
                                  data-testid={`pagar-factura-${f.ncf}`}>
                            <CreditCard className="mr-1 h-3 w-3" /> Pagar
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => visualizar(f)}
                                data-testid={`ver-factura-${f.ncf}`}>Ver</Button>
                        <Button size="sm" variant="ghost" onClick={() => descargar(f)}
                                data-testid={`descargar-factura-${f.ncf}`} title="Descargar comprobante">
                          <Download className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!pagar} onOpenChange={(o) => !o && setPagar(null)}>
        <DialogContent data-testid="modal-pago">
          <DialogHeader>
            <DialogTitle className="font-display">Pasarela de pagos</DialogTitle>
            <DialogDescription>
              {pagar?.detalle?.[0]?.concepto} · NCF {pagar?.ncf}
            </DialogDescription>
          </DialogHeader>
          <p className="font-display text-3xl font-extrabold tracking-tight">{money(pagar?.monto_total)}</p>
          <div className="mt-2 space-y-4">
            <div className="flex gap-2">
              {["tarjeta", "transferencia"].map((m) => (
                <button key={m} onClick={() => setMetodo(m)} data-testid={`metodo-${m}`}
                        className={`flex-1 rounded-md border px-4 py-3 text-sm lift ${
                          metodo === m ? "border-primary bg-primary/5 font-semibold" : "border-border"}`}>
                  {m === "tarjeta" ? "Tarjeta crédito/débito" : "Transferencia bancaria"}
                </button>
              ))}
            </div>
            {metodo === "tarjeta" && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2 sm:col-span-2">
                  <Label>Titular de la tarjeta</Label>
                  <Input placeholder="Nombre en la tarjeta" data-testid="pago-titular" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label>Número de tarjeta</Label>
                  <Input placeholder="4242 4242 4242 4242" data-testid="pago-numero" />
                </div>
              </div>
            )}
            {metodo === "transferencia" && (
              <div className="grid gap-4">
                {cuentas.length > 0 && (
                  <div className="rounded-md border border-border bg-muted/40 p-4" data-testid="cuentas-banco">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">
                      Cuentas del colegio
                    </p>
                    {cuentas.map((c, i) => (
                      <p key={i} className="mt-2 text-xs">
                        <span className="font-semibold">{c.banco}</span> · {c.tipo} · Nº {c.numero}
                        {c.titular ? ` · ${c.titular}` : ""}
                      </p>
                    ))}
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Número de referencia de la transferencia</Label>
                  <Input value={transferencia.referencia} data-testid="pago-referencia"
                         placeholder="Ej. 998877665"
                         onChange={(e) => setTransferencia({ ...transferencia, referencia: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Titular de la cuenta que transfiere</Label>
                  <Input value={transferencia.titular} data-testid="pago-titular-transferencia"
                         onChange={(e) => setTransferencia({ ...transferencia, titular: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Comprobante de la transferencia (foto o PDF)</Label>
                  <Input type="file" accept="image/*,.pdf" onChange={subirComprobante}
                         data-testid="pago-comprobante" />
                  {transferencia.comprobante_nombre && (
                    <p className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Paperclip className="h-3 w-3" /> {transferencia.comprobante_nombre}
                    </p>
                  )}
                </div>
              </div>
            )}
            <p className="rounded-md bg-muted px-4 py-3 text-xs leading-relaxed text-muted-foreground">
              {metodo === "transferencia"
                ? "Al enviar el comprobante, la factura queda en verificación hasta que la administración confirme el pago."
                : "Pasarela SIMULADA para demostración: no se procesan cobros reales. El pago se registra y se libera el comprobante fiscal."}
            </p>
            <Button className="w-full rounded-full py-6" onClick={confirmar} disabled={busy}
                    data-testid="confirmar-pago">
              {busy ? "Procesando…"
                : metodo === "transferencia" ? "Enviar comprobante de transferencia"
                  : `Pagar ${money(pagar?.monto_total)}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!ver} onOpenChange={(o) => { if (!o) { setVer(null); setPdfUrl(null); } }}>
        <DialogContent className="max-w-3xl" data-testid="modal-factura">
          <DialogHeader>
            <DialogTitle className="font-display">Comprobante fiscal {ver?.ncf}</DialogTitle>
            <DialogDescription>
              {ver?.detalle?.[0]?.concepto} · {money(ver?.monto_total)} · {ver?.estado}
            </DialogDescription>
          </DialogHeader>
          {pdfUrl ? (
            <>
              <iframe src={pdfUrl} title={`Factura ${ver?.ncf}`} data-testid="visor-factura"
                      className="h-[65vh] w-full rounded-md border border-border" />
              <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="abrir-nueva-pestana"
                 className="text-center text-xs text-primary hover:underline">
                Abrir el comprobante en una pestaña nueva
              </a>
            </>
          ) : (
            <div className="flex h-[65vh] items-center justify-center text-sm text-muted-foreground">
              Generando comprobante…
            </div>
          )}
          <Button variant="outline" className="w-full rounded-full" onClick={() => descargar(ver)}
                  data-testid="descargar-desde-visor">
            <Download className="mr-2 h-4 w-4" /> Descargar PDF
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
